import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Briefcase, Cpu, HardHat, Plane, Banknote, Shield, GraduationCap, Activity } from 'lucide-react';
import { useT } from '../../i18n/LanguageContext';
import { QATAR_OUTLINE, QATAR_BBOX } from './qatarPath';

// 8 MBK-aligned sectors; each maps to NDS3 clusters and to our venture data.
// Labels and blurbs sourced from i18n via t('wheel.sectors.<key>.label|blurb').
export const SECTORS = [
  { key: 'manufacturing', arabic: 'صِنَاعَة', Icon: HardHat,
    stats: [{ value: '3', labelKey: 'mbkVentures' }, { value: 'Cluster 1', labelKey: 'nds3Priority' }],
    companies: ['AeroForge 3D', 'Toothrocket', 'BIMS Group'] },
  { key: 'ict', arabic: 'رَقْمَنَة', Icon: Cpu,
    stats: [{ value: '4', labelKey: 'mbkVentures' }, { value: 'Cluster 2', labelKey: 'nds3Priority' }],
    companies: ['Toothmatrix', 'Entelyst', 'Braincert', 'QCloud'] },
  { key: 'healthcare', arabic: 'صِحَّة', Icon: Activity,
    stats: [{ value: '4', labelKey: 'mbkVentures' }, { value: 'Cluster 3', labelKey: 'nds3Priority' }],
    companies: ['Toothrocket', 'Toothmatrix', 'Q-Bone Precision', 'DentistsOnWheels'] },
  { key: 'logistics', arabic: 'لُوجِسْتِيَّات', Icon: Plane,
    stats: [{ value: '2nd', labelKey: 'menaLogistics' }, { value: '$5.8B', labelKey: 'contribution2020' }],
    companies: ['Qatar Aviation', 'PlayBuddy', 'Anvin'] },
  { key: 'finance', arabic: 'مَالِيَّة', Icon: Banknote,
    stats: [{ value: '7+', labelKey: 'portfolioFintechs' }, { value: 'VCaaS', labelKey: 'mbkService' }],
    companies: ['Cwallet', 'Debito', 'Manigo', 'StorFund', 'Bolt Global'] },
  { key: 'security', arabic: 'حَوْكَمَة', Icon: Shield,
    stats: [{ value: '3', labelKey: 'mbkVentures' }, { value: 'Cluster 4', labelKey: 'nds3Priority' }],
    companies: ['Vigilant Entities', 'Quantum SS', 'MBK Consulting'] },
  { key: 'education', arabic: 'تَعْلِيم', Icon: GraduationCap,
    stats: [{ value: '2', labelKey: 'mbkVentures' }, { value: 'Outcome 3', labelKey: 'nds3Outcome' }],
    companies: ['Braincert', 'PlayBuddy'] },
  { key: 'lifestyle', arabic: 'حَيَاة', Icon: Briefcase,
    stats: [{ value: '2', labelKey: 'mbkVentures' }, { value: 'Outcome 5', labelKey: 'nds3Outcome' }],
    companies: ['PlayBuddy', 'The Professional Hobbies'] },
];

export default function SectorWheel() {
  const { t } = useT();
  const [active, setActive] = useState(0);
  const [hover, setHover] = useState(null);
  const sector = SECTORS[active];

  // Auto-rotate when idle
  useEffect(() => {
    if (hover !== null) return;
    const id = setInterval(() => setActive((a) => (a + 1) % SECTORS.length), 6500);
    return () => clearInterval(id);
  }, [hover]);

  // Geometry: 600×600 viewBox, 8 equal slices starting at -90° (top)
  const N = SECTORS.length;
  const cx = 300, cy = 300;
  const rOuter = 200;     // slice outer edge
  const rInner = 95;      // inner disc boundary
  const rOrbit = 255;     // dotted ring + label orbit radius

  // Each label sits on one of two semicircular paths. To prevent clipping of
  // labels at the 3 and 9 o'clock boundaries (Healthcare/Governance), each arc
  // overhangs the horizontal axis by `overhang` radians on both ends.
  const overhang = 0.3;                      // ≈17°
  const arcSpan = Math.PI + 2 * overhang;    // ≈210° per arc
  const topStart = Math.PI - overhang;       // start angle of top arc (just below 9 o'clock)

  const slices = useMemo(() => SECTORS.map((s, i) => {
    const a0 = (-Math.PI / 2) + (i / N) * Math.PI * 2 - Math.PI / N;
    const a1 = (-Math.PI / 2) + ((i + 1) / N) * Math.PI * 2 - Math.PI / N;
    const aMid = (a0 + a1) / 2;
    const p = (a, r) => [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
    const [x1, y1] = p(a0, rOuter);
    const [x2, y2] = p(a1, rOuter);
    const [x3, y3] = p(a1, rInner);
    const [x4, y4] = p(a0, rInner);
    const path = `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 0 0 ${x4} ${y4} Z`;
    const [ix, iy] = p(aMid, (rOuter + rInner) / 2);

    // Place sector label on the appropriate arc (top vs. bottom) so it always reads upright.
    // Sectors 0,1,2,6,7 → top arc; sectors 3,4,5 → bottom arc.
    let aMidN = ((aMid % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI); // 0..2π
    const onTop = i === 0 || i === 1 || i === 2 || i === 6 || i === 7;
    let frac;
    if (onTop) {
      // Top arc spans (π-overhang) → (2π+overhang) going CW. For i=2 aMidN=0, treat as 2π.
      if (aMidN < Math.PI) aMidN += 2 * Math.PI;
      frac = (aMidN - topStart) / arcSpan;
    } else {
      // Bottom arc spans (-overhang) → (π+overhang) going CW.
      frac = (aMidN + overhang) / arcSpan;
    }
    return { path, ix, iy, useTop: onTop, frac, aMidDeg: (aMid * 180) / Math.PI };
  }), []);

  // Two ~210° arcs for the textPath rails, both at rOrbit.
  const orbitTopId = 'mbk-orbit-top';
  const _tS = [cx + Math.cos(topStart) * rOrbit, cy + Math.sin(topStart) * rOrbit];
  const _tE = [cx + Math.cos(2 * Math.PI + overhang) * rOrbit, cy + Math.sin(2 * Math.PI + overhang) * rOrbit];
  const orbitTopD = `M ${_tS[0]} ${_tS[1]} A ${rOrbit} ${rOrbit} 0 1 1 ${_tE[0]} ${_tE[1]}`;
  const orbitBotId = 'mbk-orbit-bot';
  const _bS = [cx + Math.cos(-overhang) * rOrbit, cy + Math.sin(-overhang) * rOrbit];
  const _bE = [cx + Math.cos(Math.PI + overhang) * rOrbit, cy + Math.sin(Math.PI + overhang) * rOrbit];
  const orbitBotD = `M ${_bS[0]} ${_bS[1]} A ${rOrbit} ${rOrbit} 0 1 1 ${_bE[0]} ${_bE[1]}`;

  // Qatar outline bbox from qatarPath.js. Fit into a circle of radius rInner.
  // Target height ~ rInner*1.7. Scale by min so both dims fit comfortably.
  const qScale = (rInner * 1.7) / QATAR_BBOX.h;
  const qCenterX = QATAR_BBOX.x + QATAR_BBOX.w / 2;
  const qCenterY = QATAR_BBOX.y + QATAR_BBOX.h / 2;
  const qTx = cx - qCenterX * qScale;
  const qTy = cy - qCenterY * qScale;

  const onPrev = () => setActive((a) => (a - 1 + N) % N);
  const onNext = () => setActive((a) => (a + 1) % N);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div data-testid="sector-wheel" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      {/* Wheel */}
      <div className="lg:col-span-7 relative">
        <div className="relative w-full max-w-[640px] mx-auto aspect-square">
          {/* Watermark Qatar silhouette behind wheel */}
          <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full opacity-[0.05]" aria-hidden>
            <g transform={`translate(${cx - qCenterX * qScale * 2.2} ${cy - qCenterY * qScale * 2.2}) scale(${qScale * 2.2})`}>
              <path d={QATAR_OUTLINE} fill="var(--text-primary)" />
            </g>
          </svg>

          <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full" role="img" aria-label="MBK sector wheel">
            <defs>
              <radialGradient id="inner-disc" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--bg-surface)" stopOpacity="1" />
                <stop offset="100%" stopColor="var(--bg-base)" stopOpacity="1" />
              </radialGradient>
              {/* Hidden orbital paths — labels orbit along these (top + bottom for readability) */}
              <path id={orbitTopId} d={orbitTopD} fill="none" />
              <path id={orbitBotId} d={orbitBotD} fill="none" />
            </defs>

            {/* Outer dotted orbit ring */}
            <circle cx={cx} cy={cy} r={rOrbit} fill="none" stroke="var(--brand-maroon)" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="2 5" />

            {/* Slices */}
            {slices.map((s, i) => {
              const isActive = i === active;
              const isHover = i === hover;
              const SectorIcon = SECTORS[i].Icon;
              return (
                <g
                  key={SECTORS[i].key}
                  data-testid={`sector-slice-${SECTORS[i].key}`}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <path
                    d={s.path}
                    fill={isActive ? 'rgba(125,36,59,0.18)' : isHover ? 'rgba(59,152,170,0.10)' : 'var(--bg-surface-2)'}
                    stroke="var(--border-strong)"
                    strokeWidth="1"
                    style={{ transition: 'fill 0.35s ease' }}
                  />
                  {/* Icon */}
                  <foreignObject x={s.ix - 22} y={s.iy - 22} width="44" height="44" style={{ pointerEvents: 'none' }}>
                    <div style={{
                      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: isActive ? 'var(--brand-maroon)' : 'var(--text-primary)',
                      opacity: isActive ? 1 : 0.7,
                      transition: 'color 0.3s ease, opacity 0.3s ease',
                    }}>
                      <SectorIcon size={32} strokeWidth={1.5} />
                    </div>
                  </foreignObject>
                </g>
              );
            })}

            {/* Inner disc */}
            <circle cx={cx} cy={cy} r={rInner} fill="url(#inner-disc)" stroke="var(--border-strong)" strokeWidth="1" />

            {/* Qatar map at center — solid brand maroon */}
            <g transform={`translate(${qTx} ${qTy}) scale(${qScale})`} style={{ pointerEvents: 'none' }}>
              <path
                d={QATAR_OUTLINE}
                fill="var(--brand-maroon-light)"
                stroke="rgba(248,250,252,0.5)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </g>

            {/* Slice-boundary tick dots — sit slightly inside the orbit, so they don't overlap labels */}
            {slices.map((s, i) => {
              const a0 = (-Math.PI / 2) + (i / N) * Math.PI * 2 - Math.PI / N;
              const rTick = rOrbit - 18;
              const dxp = cx + Math.cos(a0) * rTick;
              const dyp = cy + Math.sin(a0) * rTick;
              return <circle key={`tick-${i}`} cx={dxp} cy={dyp} r="2.5" fill="var(--brand-maroon)" opacity="0.7" />;
            })}

            {/* Sector labels orbiting on the dotted line (text-on-path) */}
            {slices.map((s, i) => {
              const isActive = i === active;
              const pathHref = s.useTop ? `#${orbitTopId}` : `#${orbitBotId}`;
              return (
                <g
                  key={`lbl-${SECTORS[i].key}`}
                  data-testid={`sector-label-${SECTORS[i].key}`}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <text
                    fontFamily="var(--font-display, 'Cormorant Garamond', serif)"
                    fontSize={isActive ? 22 : 18}
                    fontWeight={isActive ? 600 : 500}
                    fill={isActive ? 'var(--brand-maroon)' : 'var(--text-primary)'}
                    style={{ transition: 'fill 0.3s ease, font-size 0.3s ease', letterSpacing: '0.5px' }}
                  >
                    <textPath href={pathHref} startOffset={`${s.frac * 100}%`} textAnchor="middle">
                      {t(`wheel.sectors.${SECTORS[i].key}.label`)}
                    </textPath>
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Detail panel */}
      <div className="lg:col-span-5 surface-2 border border-subtle rounded-md p-6 md:p-8 shimmer-border relative">
        <div className="absolute top-4 right-4 flex gap-1">
          <button data-testid="sector-prev" onClick={onPrev} className="p-2 rounded-full border border-subtle text-sec hover:text-prim" aria-label={t('wheel.prev')}><ChevronLeft size={14} /></button>
          <button data-testid="sector-next" onClick={onNext} className="p-2 rounded-full border border-subtle text-sec hover:text-prim" aria-label={t('wheel.next')}><ChevronRight size={14} /></button>
        </div>
        <p className="eyebrow">0{active + 1} / 0{N} · {t('wheel.eyebrow')}</p>
        <div className="mt-3 flex items-baseline gap-3 flex-wrap">
          <h3 className="font-display text-3xl md:text-4xl text-prim tracking-tight">{t(`wheel.sectors.${sector.key}.label`)}</h3>
        </div>
        <p className="mt-4 text-sec leading-relaxed">{t(`wheel.sectors.${sector.key}.blurb`)}</p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {sector.stats.map((s) => (
            <div key={s.labelKey} className="border border-subtle bg-base p-4 rounded-md">
              <p className="font-display text-2xl text-brand-teal-light tracking-tight">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-muted">{t(`wheel.${s.labelKey}`)}</p>
            </div>
          ))}
        </div>

        {sector.companies?.length > 0 && (
          <div className="mt-6">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted mb-2">{t('wheel.mbkCompanies')}</p>
            <ul className="flex flex-wrap gap-2">
              {sector.companies.map((c) => (
                <li key={c} className="px-3 py-1.5 rounded-full border border-brand-teal/30 bg-brand-teal/5 text-brand-teal-light text-xs font-mono">{c}</li>
              ))}
            </ul>
          </div>
        )}

        <Link to="/ventures" data-testid="sector-cta" className="btn-primary mt-7 w-full justify-center">
          {t('wheel.cta')} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
